import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateProject, getProjectDetails, clearProjectDetails, getCustomers, getCostCenters, getTaxes, getPostingAccounts, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';


// edit project component
const EditProject = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectDetails, customers, customersPagination, costCenters, costCentersPagination, taxes, taxesPagination, postingAccounts, isLoading, error, success } = useSelector((s) => s.auth);
  const lastErrorRef = useRef({ message: null, time: 0 });

  const [form, setForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    revenue_account_id: null,
    wip_account_id: null,
    cost_center_id: null,
    notes: '',
  });

  // Customer State variables
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);

  // Cost Center State variables
  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Tax State variables
  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  // Revenue Account State variables
  const [revenueAccountDropdownOpen, setRevenueAccountDropdownOpen] = useState(false);
  const [revenueAccountSearchTerm, setRevenueAccountSearchTerm] = useState('');
  const revenueAccountSearchInputRef = useRef(null);
  const revenueAccountSearchTimeoutRef = useRef(null);

  // WIP Account State variables
  const [wipAccountDropdownOpen, setWipAccountDropdownOpen] = useState(false);
  const [wipAccountSearchTerm, setWipAccountSearchTerm] = useState('');
  const wipAccountSearchInputRef = useRef(null);
  const wipAccountSearchTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(getProjectDetails(id));
    dispatch(clearSuccess());
    dispatch(clearError());
    return () => {
      dispatch(clearProjectDetails());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (projectDetails) {
      setForm({
        code: projectDetails.code || '',
        name_ar: projectDetails.name_ar || '',
        name_en: projectDetails.name_en || '',
        customer_id: projectDetails.customer_id || null,
        start_date: projectDetails.start_date ? String(projectDetails.start_date).split('T')[0] : '',
        end_date: projectDetails.end_date ? String(projectDetails.end_date).split('T')[0] : '',
        status: projectDetails.status || 'planned',
        contract_value: projectDetails.contract_value != null ? projectDetails.contract_value : '',
        tax_id: projectDetails.tax_id || null,
        revenue_account_id: projectDetails.revenue_account_id || null,
        wip_account_id: projectDetails.wip_account_id || null,
        cost_center_id: projectDetails.cost_center_id || null,
        notes: projectDetails.notes || '',
      });
    }
  }, [projectDetails]);

  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCurrentPage, customerDropdownOpen, customerSearchTerm]);

  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchTerm, customerDropdownOpen]);

  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  useEffect(() => {
    if (costCenterSearchTerm !== undefined && costCenterDropdownOpen) {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
      costCenterSearchTimeoutRef.current = setTimeout(() => {
        setCostCenterCurrentPage(1);
        dispatch(getCostCenters({ page: 1, per_page: 10, search: costCenterSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterSearchTerm, costCenterDropdownOpen]);

  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  useEffect(() => {
    if (taxSearchTerm !== undefined && taxDropdownOpen) {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
      taxSearchTimeoutRef.current = setTimeout(() => {
        setTaxCurrentPage(1);
        dispatch(getTaxes({ page: 1, per_page: 10, search: taxSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (taxSearchTimeoutRef.current) clearTimeout(taxSearchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxSearchTerm, taxDropdownOpen]);

  useEffect(() => {
    if (revenueAccountDropdownOpen || wipAccountDropdownOpen) {
      dispatch(getPostingAccounts({ search: revenueAccountSearchTerm || wipAccountSearchTerm || undefined }));
    }
  }, [dispatch, revenueAccountDropdownOpen, wipAccountDropdownOpen]);

  useEffect(() => {
    if (revenueAccountSearchTerm !== undefined && revenueAccountDropdownOpen) {
      if (revenueAccountSearchTimeoutRef.current) clearTimeout(revenueAccountSearchTimeoutRef.current);
      revenueAccountSearchTimeoutRef.current = setTimeout(() => {
        dispatch(getPostingAccounts({ search: revenueAccountSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (revenueAccountSearchTimeoutRef.current) clearTimeout(revenueAccountSearchTimeoutRef.current);
    };
  }, [revenueAccountSearchTerm, revenueAccountDropdownOpen, dispatch]);

  useEffect(() => {
    if (wipAccountSearchTerm !== undefined && wipAccountDropdownOpen) {
      if (wipAccountSearchTimeoutRef.current) clearTimeout(wipAccountSearchTimeoutRef.current);
      wipAccountSearchTimeoutRef.current = setTimeout(() => {
        dispatch(getPostingAccounts({ search: wipAccountSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (wipAccountSearchTimeoutRef.current) clearTimeout(wipAccountSearchTimeoutRef.current);
    };
  }, [wipAccountSearchTerm, wipAccountDropdownOpen, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      navigate('/projects');
    }
  }, [success, dispatch, navigate]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name_ar || !form.contract_value) {
      toast.error('برجاء استكمال الحقول المطلوبة', { rtl: true });
      return;
    }
    const payload = {
      code: form.code || undefined,
      name_ar: form.name_ar,
      name_en: form.name_en || undefined,
      customer_id: form.customer_id || undefined,
      start_date: form.start_date || undefined,
      end_date: form.end_date || undefined,
      status: form.status,
      contract_value: Number(form.contract_value),
      tax_id: form.tax_id || undefined,
      revenue_account_id: form.revenue_account_id || undefined,
      wip_account_id: form.wip_account_id || undefined,
      cost_center_id: form.cost_center_id || undefined,
      notes: form.notes || undefined,
    };
    dispatch(updateProject({ id, data: payload }));
  };

  const renderDropdownItem = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = item[nameArField] || item[nameEnField] || '';
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderCustomerDisplay = () => {
    if (!form.customer_id) return 'اختر العميل';
    const selected = (customers || []).find((c) => String(c.id) === String(form.customer_id));
    return selected ? renderDropdownItem(selected, 'code', 'name_ar', 'name') : 'اختر العميل';
  };

  const renderCostCenterDisplay = () => {
    if (!form.cost_center_id) return 'اختر مركز التكلفة';
    const selected = (costCenters || []).find((cc) => String(cc.id) === String(form.cost_center_id));
    return selected ? renderDropdownItem(selected, 'code', 'name_ar', 'name') : 'اختر مركز التكلفة';
  };

  const renderTaxDisplay = () => {
    if (!form.tax_id) return 'اختر الضريبة';
    const selected = (taxes || []).find((t) => String(t.id) === String(form.tax_id));
    return selected ? `${selected.name_ar || selected.name_en || ''}` : 'اختر الضريبة';
  };

  const renderAccountDisplay = (accountId, accounts) => {
    if (!accountId) return 'اختر الحساب';
    const selected = (accounts || []).find((a) => String(a.id) === String(accountId));
    return selected ? renderDropdownItem(selected, 'code', 'name_ar', 'name') : 'اختر الحساب';
  };

  const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label }) => {
    if (!isOpen) return null;
    return (
      <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
        <div style={{ padding: 8 }}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder={`ابحث عن ${label}`}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
          />
        </div>
        {pagination && pagination.last_page >= 1 && (
          <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#ccc', fontSize: 11 }}>الصفحة {pagination.current_page} من {pagination.last_page}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
              {(() => {
                const pages = [];
                const current = pagination.current_page;
                const last = pagination.last_page;
                const showPages = 5;
                let start = Math.max(1, current - Math.floor(showPages / 2));
                let end = Math.min(last, start + showPages - 1);
                if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map((page) => (
                  <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
                ));
              })()}
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
              <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
            </div>
          </div>
        )}
        <div>
          {(Array.isArray(items) ? items : []).map((item) => (
            <div key={item.id} onClick={() => onSelect(item.id)} style={{ padding: 10, cursor: 'pointer', borderTop: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{renderDropdownItem(item, 'code', 'name_ar', 'name')}</span>
            </div>
          ))}
          {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>لا توجد نتائج</div>}
        </div>
      </div>
    );
  };

  const filteredAccounts = (accounts, searchTerm) => {
    if (!searchTerm) return accounts || [];
    const term = searchTerm.toLowerCase();
    return (accounts || []).filter((a) => {
      const code = (a.code || '').toLowerCase();
      const nameAr = (a.name_ar || '').toLowerCase();
      const nameEn = (a.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <h1 style={{ marginTop: 0, marginBottom: 16, fontSize: '20px' }}>تعديل مشروع</h1>
      <form onSubmit={handleSubmit} style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 24, display: 'grid', gap: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الاسم بالعربية <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="text" name="name_ar" value={form.name_ar} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الاسم بالإنجليزية</label>
            <input type="text" name="name_en" value={form.name_en} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>الحالة</label>
            <select name="status" value={form.status} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }}>
              <option value="planned">مخطط</option>
              <option value="in_progress">قيد التنفيذ</option>
              <option value="paused">متوقف</option>
              <option value="completed">مكتمل</option>
              <option value="cancelled">ملغى</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>قيمة العقد <span style={{ color: '#dc3545' }}>*</span></label>
            <input type="number"  name="contract_value" value={form.contract_value} onChange={handleChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14 }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>ملاحظات</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, resize: 'vertical' }} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <button type="button" onClick={() => navigate('/projects')} style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>إلغاء</button>
          <button type="submit" disabled={isLoading} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 8, padding: '10px 16px', cursor: 'pointer' }}>حفظ</button>
        </div>
      </form>
    </div>
  );
};

export default EditProject;

